/**
 * @module SettingsIMCCalculator
 * @description BMI and macro calculator. Computes BMI using Mifflin-St Jeor
 * equation and suggests daily calories/macros based on activity level and goal.
 */
import React, { useState, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calculator, Scale, Ruler, Activity, Target, Loader2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import YumixTip from '../YumixTip';
import { useCoachTip } from '../../constants/coachTips';

/** @constant Activity levels with Mifflin-St Jeor multipliers */
const ACTIVITY_LEVELS = {
  fr: [
    { value: 'sedentary', label: 'Sedentaire (bureau)', factor: 1.2 },
    { value: 'light', label: 'Leger (1-3x/sem)', factor: 1.375 },
    { value: 'moderate', label: 'Modere (3-5x/sem)', factor: 1.55 },
    { value: 'intense', label: 'Intense (6-7x/sem)', factor: 1.725 },
    { value: 'elite', label: 'Elite (2x/jour)', factor: 1.9 },
  ],
  en: [
    { value: 'sedentary', label: 'Sedentary (desk job)', factor: 1.2 },
    { value: 'light', label: 'Light (1-3x/week)', factor: 1.375 },
    { value: 'moderate', label: 'Moderate (3-5x/week)', factor: 1.55 },
    { value: 'intense', label: 'Intense (6-7x/week)', factor: 1.725 },
    { value: 'elite', label: 'Elite (2x/day)', factor: 1.9 },
  ],
};

/** @constant NEAT — Job activity factor (Iter. 385) — exported so Settings.js
 * can reuse the same single source of truth for the standalone profile selector. */
export const JOB_PROFILES = {
  fr: [
    { value: 'desk',     label: 'Bureau (sédentaire)',           factor: 1.00 },
    { value: 'light',    label: 'Debout léger (enseignant, vente)', factor: 1.15 },
    { value: 'moderate', label: 'Physique modéré (chef, infirmier)', factor: 1.30 },
    { value: 'intense',  label: 'Physique intense (construction)',   factor: 1.45 },
  ],
  en: [
    { value: 'desk',     label: 'Desk job',                          factor: 1.00 },
    { value: 'light',    label: 'Standing light (teacher, retail)',  factor: 1.15 },
    { value: 'moderate', label: 'Physical mod (chef, nurse)',        factor: 1.30 },
    { value: 'intense',  label: 'Physical heavy (construction)',     factor: 1.45 },
  ],
};

const SettingsIMCCalculator = memo(function SettingsIMCCalculator() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const neatTip = useCoachTip('macros.neat');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    weight_kg: user?.current_weight_lbs ? (user.current_weight_lbs / 2.20462).toFixed(1) : '',
    height_cm: user?.height_cm || '',
    age: user?.age || '',
    sex: user?.sex || '',
    activity_level: user?.activity_level || '',
    job_activity_level: user?.job_activity_level || 'desk',
    goal: user?.goal || 'maintain',
  });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  /**
   * Calculate BMI and macro targets using Mifflin-St Jeor equation.
   * Iter. 406.fr.30 — Computes carbs_g / fats_g too (same split as backend
   * `calculate_deficit_macros`: protein priority via 1.8 g/kg target weight,
   * fat at 28 % of target_calories, carbs = remainder). Without this the
   * applied macros are incoherent (calories ≠ 4P + 9F + 4C).
   * @returns {void}
   */
  const calculate = () => {
    const w = parseFloat(form.weight_kg);
    const h = parseFloat(form.height_cm);
    const a = parseInt(form.age);
    if (!w || !h || !a || !form.sex) {
      toast.error(language === 'fr' ? 'Remplissez tous les champs' : 'Fill all fields');
      return;
    }

    const imc = (w / ((h / 100) ** 2)).toFixed(1);
    const categories = [
      { max: 18.5, label: language === 'fr' ? 'Insuffisance' : 'Underweight', color: 'text-blue-400', bg: 'bg-blue-500/10' },
      { max: 24.9, label: language === 'fr' ? 'Normal' : 'Normal', color: 'text-green-400', bg: 'bg-green-500/10' },
      { max: 29.9, label: language === 'fr' ? 'Surpoids' : 'Overweight', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
      { max: 999, label: language === 'fr' ? 'Obesite' : 'Obese', color: 'text-red-400', bg: 'bg-red-500/10' },
    ];
    const imcCategory = categories.find(c => parseFloat(imc) <= c.max);

    let bmr = form.sex === 'male'
      ? 10 * w + 6.25 * h - 5 * a + 5
      : 10 * w + 6.25 * h - 5 * a - 161;

    const activities = ACTIVITY_LEVELS[language] || ACTIVITY_LEVELS.fr;
    const activity = activities.find(al => al.value === form.activity_level);
    const jobs = JOB_PROFILES[language] || JOB_PROFILES.fr;
    const job = jobs.find(j => j.value === (form.job_activity_level || 'desk'));
    const sportFactor = activity?.factor || 1.55;
    const jobFactor = job?.factor || 1.0;
    const tdee = Math.round(bmr * sportFactor * jobFactor);

    const goalAdjust = { lose: -500, maintain: 0, gain: 300 };
    let target = tdee + (goalAdjust[form.goal] || 0);
    // 1200 kcal hard floor (same as backend SAFETY_FLOOR_KCAL)
    if (target < 1200) target = 1200;

    // Protein band (g/day) based on bodyweight
    const protein_min = Math.round(w * 1.6);
    const protein_max = Math.round(w * 2.2);
    // Performance protein target: 1.8 g/kg bodyweight (matches backend default)
    const protein_target = Math.max(protein_min, Math.min(protein_max, Math.round(w * 1.8)));

    // Fats: 28 % of target calories (matches backend calculate_deficit_macros)
    const fats_g = Math.round((target * 0.28) / 9);
    // Carbs: remainder
    const carbs_cal = target - (protein_target * 4) - (fats_g * 9);
    const carbs_g = Math.max(50, Math.round(carbs_cal / 4));

    setResult({
      imc,
      imcCategory,
      bmr: Math.round(bmr),
      tdee,
      targetCalories: target,
      protein_min_g: protein_min,
      protein_max_g: protein_max,
      protein_target_g: protein_target,
      carbs_g,
      fats_g,
      fiber_g: 35,
    });
  };

  const apply = async () => {
    // Iter. 406.fr.30 — No more "click twice" bug: clicking the button when
    // no preview exists triggers a fresh calculation, then the user clicks
    // again to apply. The button label switches to "Appliquer" once `result`
    // is populated, so the intent is always explicit.
    if (!result) { calculate(); return; }
    setSaving(true);
    try {
      const updates = {
        imc: result.imc,
        daily_calories: result.targetCalories,
        protein_min_g: result.protein_min_g,
        protein_max_g: result.protein_max_g,
        carbs_target_g: result.carbs_g,
        fats_target_g: result.fats_g,
        fiber_target_g: result.fiber_g,
        bmr: result.bmr,
        tdee: result.tdee,
        height_cm: parseFloat(form.height_cm),
        age: parseInt(form.age),
        sex: form.sex,
        activity_level: form.activity_level,
        job_activity_level: form.job_activity_level || 'desk',
        goal: form.goal,
        current_weight_lbs: Math.round(parseFloat(form.weight_kg) * 2.20462 * 10) / 10,
      };
      await api.put('/users/profile', updates);
      // Iter. 406.fr.30 — Also push ALL macro fields into the AuthContext so
      // SettingsMacroTargets re-syncs immediately (its useEffect watches the
      // same keys). Without this, the displayed macros stayed stale.
      updateUser({ ...user, ...updates });
      toast.success(language === 'fr' ? 'IMC et macros mis a jour!' : 'BMI and macros updated!');
      setOpen(false);
      setResult(null);
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          {language === 'fr' ? 'Calculateur IMC & Macros' : 'BMI & Macros Calculator'}
        </CardTitle>
        <CardDescription>
          {language === 'fr'
            ? 'Recalculez vos besoins nutritionnels selon votre IMC et niveau d\'activite'
            : 'Recalculate your nutritional needs based on your BMI and activity level'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!open ? (
          <div className="text-center py-4">
            {user?.imc && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Votre IMC actuel' : 'Your current BMI'}</p>
                <p className="font-heading text-3xl text-primary">{user.imc}</p>
              </div>
            )}
            <Button onClick={() => setOpen(true)} variant="outline" className="border-primary text-primary">
              <Calculator className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Recalculer mes macros' : 'Recalculate my macros'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Scale className="w-4 h-4 text-primary" /> {language === 'fr' ? 'Poids (kg)' : 'Weight (kg)'}</Label>
                <Input type="number" value={form.weight_kg} onChange={e => setForm({...form, weight_kg: e.target.value})} className="bg-secondary border-border" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Ruler className="w-4 h-4 text-primary" /> {language === 'fr' ? 'Taille (cm)' : 'Height (cm)'}</Label>
                <Input type="number" value={form.height_cm} onChange={e => setForm({...form, height_cm: e.target.value})} className="bg-secondary border-border" />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Age' : 'Age'}</Label>
                <Input type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} className="bg-secondary border-border" />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Sexe' : 'Sex'}</Label>
                <Select value={form.sex} onValueChange={v => setForm({...form, sex: v})}>
                  <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{language === 'fr' ? 'Homme' : 'Male'}</SelectItem>
                    <SelectItem value="female">{language === 'fr' ? 'Femme' : 'Female'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /> {language === 'fr' ? 'Niveau d\'activite' : 'Activity Level'}</Label>
              <Select value={form.activity_level} onValueChange={v => setForm({...form, activity_level: v})}>
                <SelectTrigger className="bg-secondary border-border"><SelectValue placeholder={language === 'fr' ? 'Selectionner' : 'Select'} /></SelectTrigger>
                <SelectContent>
                  {(ACTIVITY_LEVELS[language] || ACTIVITY_LEVELS.fr).map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2" data-testid="settings-imc-job-profile">
              <Label className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-orange-400" />
                {language === 'fr' ? 'Profil professionnel' : language === 'es' ? 'Perfil profesional' : 'Job profile'}
                {neatTip && (
                  <YumixTip
                    id={neatTip.id}
                    language={neatTip.language}
                    size="sm"
                    side="top"
                    title={neatTip.title}
                    text={neatTip.text}
                  />
                )}
              </Label>
              <Select value={form.job_activity_level} onValueChange={v => setForm({...form, job_activity_level: v})}>
                <SelectTrigger className="bg-secondary border-border" data-testid="settings-imc-job-trigger">
                  <SelectValue placeholder={language === 'fr' ? 'Sédentaire (bureau)' : 'Desk job'} />
                </SelectTrigger>
                <SelectContent>
                  {(JOB_PROFILES[language] || JOB_PROFILES.fr).map(j => (
                    <SelectItem key={j.value} value={j.value} data-testid={`settings-imc-job-${j.value}`}>
                      {j.label} <span className="text-muted-foreground">×{j.factor}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                {language === 'fr'
                  ? 'Corrige ton TDEE selon ta journée de travail (debout, charges…).'
                  : language === 'es'
                  ? 'Corrige tu TDEE según tu jornada laboral.'
                  : 'Adjusts your TDEE for standing/heavy work shifts.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> {language === 'fr' ? 'Objectif' : 'Goal'}</Label>
              <Select value={form.goal} onValueChange={v => setForm({...form, goal: v})}>
                <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lose">{language === 'fr' ? 'Perdre du poids' : 'Lose weight'}</SelectItem>
                  <SelectItem value="maintain">{language === 'fr' ? 'Maintenir' : 'Maintain'}</SelectItem>
                  <SelectItem value="gain">{language === 'fr' ? 'Prendre du muscle' : 'Build muscle'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {result && (
              <div className={`p-4 rounded-xl ${result.imcCategory?.bg || 'bg-secondary'}`} data-testid="imc-result-preview">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">{language === 'fr' ? 'Votre IMC' : 'Your BMI'}</span>
                  <span className={`font-heading text-2xl font-bold ${result.imcCategory?.color || ''}`}>{result.imc}</span>
                </div>
                <p className={result.imcCategory?.color}>{result.imcCategory?.label}</p>
                <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">{language === 'fr' ? 'BMR :' : 'BMR:'}</span> <strong>{result.bmr}</strong></div>
                  <div><span className="text-muted-foreground">{language === 'fr' ? 'TDEE :' : 'TDEE:'}</span> <strong>{result.tdee}</strong></div>
                  <div className="col-span-2"><span className="text-muted-foreground">Calories cibles :</span> <strong className="text-primary" data-testid="imc-result-calories">{result.targetCalories}</strong></div>
                  <div><span className="text-muted-foreground">Proteines :</span> <strong>{result.protein_min_g}-{result.protein_max_g}g</strong></div>
                  <div><span className="text-muted-foreground">Cible prot :</span> <strong>{result.protein_target_g}g</strong></div>
                  <div><span className="text-muted-foreground">Glucides :</span> <strong className="text-blue-400" data-testid="imc-result-carbs">{result.carbs_g}g</strong></div>
                  <div><span className="text-muted-foreground">Lipides :</span> <strong className="text-red-400" data-testid="imc-result-fats">{result.fats_g}g</strong></div>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={apply} disabled={saving} className="flex-1 bg-primary text-primary-foreground" data-testid="imc-calculate-or-apply-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                {result
                  ? (language === 'fr' ? 'Appliquer ces macros' : 'Apply these macros')
                  : (language === 'fr' ? 'Calculer' : 'Calculate')}
              </Button>
              <Button variant="outline" onClick={() => { setOpen(false); setResult(null); }}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default SettingsIMCCalculator;
